import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import DebtService from '../../services/debt.service';
import HistoryLogService from '../../services/historyLog.service';
import { showCurrency } from '../../utils';
import { getEntityLabel, getFieldLabel } from '../../constants/historyTranslations';
import { KvGrid, ProgressBar, Timeline, EmptyState, Button } from '../../components/EntityList';
import { isCancelledByClose } from '../../utils/dueCancellation';

const DUES_PAGE_SIZE = 12;

export function formatDate(date) {
	return date ? moment.utc(date).format('DD/MM/YYYY') : '';
}

export function formatMoney(currency, amount) {
	if (amount === null || amount === undefined) {
		return '';
	}
	return `${showCurrency(currency) || ''} ${Number(amount).toLocaleString('es-UY')}`.trim();
}

export function useDues(debtId, enabled) {
	const [dues, setDues] = useState([]);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (!enabled || !debtId) {
			return;
		}
		let isCurrent = true;
		setLoading(true);
		DebtService.getDuesForDebt(debtId)
			.then(({ data }) => {
				if (!isCurrent) {
					return;
				}
				// The route returns dues sorted by date desc; the drawer's
				// Cronograma reads chronologically, so reverse it here.
				const ascending = [...data].reverse();
				setDues(ascending);
			})
			.catch(() => {
				if (isCurrent) {
					setDues([]);
				}
			})
			.finally(() => {
				if (isCurrent) {
					setLoading(false);
				}
			});
		return () => {
			isCurrent = false;
		};
	}, [debtId, enabled]);

	return { dues, loading };
}

export function useActivity(recordId, enabled) {
	const [items, setItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const [forbidden, setForbidden] = useState(false);

	useEffect(() => {
		if (!enabled || !recordId) {
			return;
		}
		let isCurrent = true;
		setLoading(true);
		setForbidden(false);
		HistoryLogService.listFromId(recordId)
			.then(({ data }) => {
				if (isCurrent) {
					setItems(data || []);
				}
			})
			.catch((error) => {
				// Non-admins get a 403 here (GET /historyLogs/:id is admin-gated) —
				// that's an empty state, not an error, per the detail-page spec.
				if (isCurrent) {
					setItems([]);
					setForbidden(error?.response?.status === 403);
				}
			})
			.finally(() => {
				if (isCurrent) {
					setLoading(false);
				}
			});
		return () => {
			isCurrent = false;
		};
	}, [recordId, enabled]);

	return { items, loading, forbidden };
}

const isEmptyValue = (value) => value === undefined || value === null || value === '';

function describeCambio(cambio, modelo) {
	const field = getFieldLabel(modelo, cambio.campo);
	const nuevo = cambio.valorNuevoDisplay || 'N/A';
	if (isEmptyValue(cambio.valorAnterior)) {
		return `Asignado ${nuevo} a ${field}`;
	}
	const anterior = cambio.valorAnteriorDisplay || 'N/A';
	return `${field} cambió de ${anterior} a ${nuevo}`;
}

// A historyLog entry looks like { operacion: 'insert'|'update'|'delete', modelo,
// cambios: [{campo, valorAnterior, valorNuevo, ...}], responsable, createdAt }
// — see HistoryModal.js's renderHistoryLogs for the canonical read of this shape.
function historyLogToTimelineItem(log) {
	if (log.operacion === 'insert') {
		return { date: formatDate(log.createdAt), title: `Se ha creado ${getEntityLabel(log.modelo)}`, dot: 'accent' };
	}
	if (log.operacion === 'delete') {
		return { date: formatDate(log.createdAt), title: `Se ha eliminado ${getEntityLabel(log.modelo)}`, dot: 'danger' };
	}
	const sub = (log.cambios || []).map((cambio) => describeCambio(cambio, log.modelo)).join(' · ');
	return {
		date: formatDate(log.createdAt),
		title: `Modificación realizada por ${log.responsable ? log.responsable.name : ''}`,
		sub,
		dot: 'ok',
	};
}

export function useActivityTimeline(recordId, enabled) {
	const { items, loading, forbidden } = useActivity(recordId, enabled);
	const timelineItems = useMemo(() => items.map(historyLogToTimelineItem), [items]);
	return { timelineItems, loading, forbidden };
}

function DebtBody({ debt }) {
	const [visibleCount, setVisibleCount] = useState(DUES_PAGE_SIZE);
	const { dues, loading: duesLoading } = useDues(debt._id, true);
	const { timelineItems, loading: activityLoading } = useActivityTimeline(debt._id, true);

	return (
		<>
			<section>
				<h3 className='tw:mb-3 tw:text-[13px] tw:font-semibold tw:text-ink-0'>Detalle</h3>
				<KvGrid
					rows={[
						{ label: 'Concepto', value: debt.concept },
						{ label: 'Capital', value: formatMoney(debt.currency, debt.capital), mono: true },
						{ label: 'Monto cuota', value: formatMoney(debt.currency, debt.amount), mono: true },
						{ label: 'Inicio', value: formatDate(debt.date), mono: true },
						{ label: 'Fin', value: formatDate(debt.endDate), mono: true },
						{ label: 'Saldo', value: formatMoney(debt.currency, debt.balance), mono: true, strong: true },
					]}
				/>
				<div className='tw:mt-3'>
					<ProgressBar value={debt.generatedDebts?.length || 0} max={debt.dues || 0} />
					<div className='tw:mt-1 tw:text-[12px] tw:text-ink-3'>
						{debt.generatedDebts?.length || 0}/{debt.dues || 0} cuotas generadas
					</div>
				</div>
			</section>

			<section>
				<h3 className='tw:mb-3 tw:text-[13px] tw:font-semibold tw:text-ink-0'>Cronograma</h3>
				{duesLoading ? (
					<div className='tw:text-[12.5px] tw:text-ink-3'>Cargando...</div>
				) : dues.length === 0 ? (
					<EmptyState title='Sin cuotas generadas' />
				) : (
					<div className='tw:flex tw:flex-col tw:gap-1.5'>
						{dues.slice(0, visibleCount).map((due, index) => {
							const isCancelled = isCancelledByClose(due);
							const isPendingCount = due.billingStatus === 'pendingCount';
							const isPaid = due.paid && !isPendingCount && !isCancelled;
							const isOverdue = !isPaid && !isCancelled && due.expiration && moment.utc(due.expiration).isBefore(moment.utc());
							return (
								<div
									key={due._id}
									className='tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-border tw:py-2 tw:text-[13px] tw:last:border-b-0'
								>
									<span className='tw:text-ink-3'>#{index + 1}</span>
									<span className='tw:flex-1 tw:text-ink-1'>{formatDate(due.expiration)}</span>
									<span className='tw:font-mono tw:tabular-nums'>{formatMoney(debt.currency, due.amount)}</span>
									<span
										className={
											isPendingCount || isCancelled
												? 'tw:text-ink-3'
												: isPaid
													? 'tw:text-ok'
													: isOverdue
														? 'tw:text-danger'
														: 'tw:text-ink-2'
										}
									>
										{isCancelled ? 'Cancelado' : isPendingCount ? 'Pendiente conteo' : isPaid ? 'Pagada' : isOverdue ? 'Vencida' : 'Pendiente'}
									</span>
								</div>
							);
						})}
						{visibleCount < dues.length && (
							<Button size='sm' onClick={() => setVisibleCount((count) => count + DUES_PAGE_SIZE)}>
								Ver más
							</Button>
						)}
					</div>
				)}
			</section>

			<section>
				<h3 className='tw:mb-3 tw:text-[13px] tw:font-semibold tw:text-ink-0'>Actividad</h3>
				{activityLoading ? (
					<div className='tw:text-[12.5px] tw:text-ink-3'>Cargando...</div>
				) : timelineItems.length === 0 ? (
					<EmptyState title='Sin actividad registrada' description='No hay cambios de historial disponibles para esta deuda.' />
				) : (
					<Timeline items={timelineItems} />
				)}
			</section>
		</>
	);
}

export default DebtBody;
